import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { Id } from 'ev-types';

import { FormTrigger } from 'ev-api/core/customFormSubmissions';

export enum FormStatusCardVariant {
  Available = 'available',
  InProgress = 'in-progress',
  Complete = 'complete',
  Submitted = 'submitted',
}

export type CustomFormState = {
  [key in FormTrigger]?: {
    formId: string | null;
    formVersionId: string | null;
    formAction: 'fill' | 'share' | 'view' | 'historical' | null;
    formState: FormStatusCardVariant | null;
    submissionId: string | Id | null;
    isDirty: boolean;
    keepLock: boolean;
  };
};

const initialState: CustomFormState = {
  [FormTrigger.SharedForms]: {
    formId: null,
    formVersionId: null,
    formAction: null,
    formState: FormStatusCardVariant.Available,
    submissionId: null,
    isDirty: false,
    keepLock: false,
  },
  [FormTrigger.VisitNotes]: {
    formId: null,
    formVersionId: null,
    formAction: null,
    formState: FormStatusCardVariant.Available,
    submissionId: null,
    isDirty: false,
    keepLock: false,
  },
};

export const customFormSidebarSlice = createSlice({
  name: 'customFormSidebar',
  initialState,
  reducers: {
    openCustomFormSidebar: (
      state,
      action: PayloadAction<{
        formId: string;
        formVersionId?: string;
        formAction: 'fill' | 'share' | 'view' | 'historical' | null;
        formState: FormStatusCardVariant | null;
        submissionId?: string | Id | null;
        formTrigger: FormTrigger;
        isDirty?: boolean;
        keepLook?: boolean;
      }>,
    ) => {
      const trigger = action.payload.formTrigger;
      state[trigger] = {
        formId: action.payload.formId,
        formVersionId: action.payload.formVersionId || null,
        formAction: action.payload.formAction,
        formState: action.payload.formState,
        submissionId: action.payload.submissionId ?? null,
        isDirty: action.payload.isDirty || false,
        keepLock: action.payload.keepLook || false,
      };
    },

    updateDirtyState: (
      state,
      action: PayloadAction<{
        formTrigger: FormTrigger;
        isDirty: boolean;
        keepLock: boolean;
      }>,
    ) => {
      const trigger = action.payload.formTrigger;
      if (state[trigger]) {
        state[trigger].isDirty = action.payload.isDirty || false;
        state[trigger].keepLock = action.payload.keepLock || false;
      }
    },

    updateKeepLock: (
      state,
      action: PayloadAction<{
        formTrigger: FormTrigger;
        keepLock: boolean;
      }>,
    ) => {
      const trigger = action.payload.formTrigger;
      if (state[trigger]) {
        state[trigger].keepLock = action.payload.keepLock || false;
      }
    },
    partialClearCustomFormSidebar: (
      state,
      action: PayloadAction<{
        formTrigger: FormTrigger;
      }>,
    ) => {
      const trigger = action.payload.formTrigger;
      state[trigger] = {
        formId: null,
        formVersionId: null,
        formAction: null,
        formState: null,
        submissionId: null,
        isDirty: false,
        keepLock: false,
      };
    },
    clearCustomFormSidebar() {
      return initialState;
    },
  },
});
